import { captureError } from '@repo/common/utils/web-tracing';
import { isNil } from 'lodash-es';
import type { PluginSimple } from 'markdown-it';
import { baseKeymap } from 'prosemirror-commands';
import { dropCursor } from 'prosemirror-dropcursor';
import { gapCursor } from 'prosemirror-gapcursor';
import type { InputRule } from 'prosemirror-inputrules';
import { inputRules } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import type { MarkdownParser, MarkdownSerializer } from 'prosemirror-markdown';
import type {
	MarkSpec,
	NodeSpec,
	Node as ProsemirrorNode,
} from 'prosemirror-model';
import { Schema } from 'prosemirror-model';
import type { Plugin, Transaction } from 'prosemirror-state';
import { EditorState, Selection } from 'prosemirror-state';
import { AddMarkStep, RemoveMarkStep } from 'prosemirror-transform';
import type { Decoration, NodeViewConstructor } from 'prosemirror-view';
import { EditorView } from 'prosemirror-view';
import type {
	ISecodaEditor,
	SecodaEditorComponentProps,
	SecodaEditorProps,
} from '.';
import ComponentView from './components/ComponentView';
import type { NodeViewRenderer } from './components/NodeViewRenderer';
import type Extension from './lib/Extension';
import type { CommandFactory } from './lib/Extension';
import ExtensionManager from './lib/ExtensionManager';
import { getCommentFromId } from './lib/getCommentFromId';
import { getHeadings } from './lib/getHeadings';
import { getQueryBlocks } from './lib/getQueryBlocks';
import Code from './marks/Code';
import type Mark from './marks/Mark';
import Doc from './nodes/Doc';
import type Node from './nodes/Node';
import Paragraph from './nodes/Paragraph';
import ProseMirrorReactNode from './nodes/ReactNode';
import Text from './nodes/Text';
import PreventTab from './plugins/PreventTab';
import light from './styles/theme';
import { Toolbar } from './types';

export class SecodaEditor implements ISecodaEditor {
	extensions: ExtensionManager;
	private nodes: { [name: string]: NodeSpec };
	private marks: { [name: string]: MarkSpec };
	schema: Schema;
	plugins: Plugin[];
	private rulePlugins: PluginSimple[];
	private keymaps: Plugin[];
	inputRules: InputRule[];
	serializer: MarkdownSerializer;
	parser: MarkdownParser;
	pasteParser: MarkdownParser;
	private state?: EditorState;
	nodeViews: { [name: string]: NodeViewConstructor };
	commands: Record<string, CommandFactory> = {};
	renderers: Set<NodeViewRenderer<SecodaEditorComponentProps>> = new Set();
	// Track all ComponentView instances so we can force-update them when readOnly changes.
	// ComponentViews register themselves in their constructor and unregister in destroy().
	componentViews: Set<ComponentView> = new Set();
	widgets: Record<string, () => React.ReactElement | null> = {};
	options: SecodaEditorProps;
	view?: EditorView;
	openedToolbar: Toolbar | null = null;
	onStateChange?: () => void;
	blockMenuSearch?: string;

	constructor(props: SecodaEditorProps, onStateChange?: () => void) {
		const {
			extensions = [],
			disableExtensions = [],
			disableInputExtensions = [],
		} = props;
		this.onStateChange = onStateChange;

		this.options = props;
		this.extensions = this.createExtensions(
			extensions,
			disableExtensions,
			disableInputExtensions
		);
		this.nodes = this.createNodes();
		this.marks = this.createMarks();
		this.schema = this.createSchema();
		this.plugins = this.createPlugins();
		this.rulePlugins = this.createRulePlugins();
		this.inputRules = this.createInputRules();
		this.keymaps = this.createKeymaps();
		// @ts-expect-error TS(2739): Type 'MarkdownSerializer' is missing the following... Remove this comment to see the full error message
		this.serializer = this.createSerializer();
		this.parser = this.createParser();
		this.pasteParser = this.createPasteParser();
		this.nodeViews = this.createNodeViews();
		this.widgets = this.createWidgets();
	}

	setValue(value: string) {
		if (!this.view) {
			throw new Error('Editor state is not initialized');
		}

		const newState = this.createState(value);
		this.view.updateState(newState);

		return this;
	}

	setReadOnly(readOnly?: boolean) {
		this.options = {
			...this.options,
			readOnly,
		};

		if (this.view) {
			this.view.update({
				...this.view.props,
				editable: () => !readOnly,
			});

			// Force update all ComponentViews to reflect the new readOnly state.
			// ComponentViews register themselves when created, so we iterate through
			// all registered views and force them to update their React props.
			// This ensures React components receive the updated isEditable prop and re-render.
			for (const componentView of this.componentViews) {
				componentView.forceUpdate();
			}
		}

		return this;
	}

	dispatchTransaction(fn: (tr: Transaction) => void) {
		if (!this.state) {
			throw new Error('Editor state is not initialized');
		}

		const { tr } = this.state;
		fn(tr);
		this.state = this.state?.apply(tr);

		return this;
	}

	serializeToMarkdown() {
		if (!this.view?.state) {
			throw new Error('Editor state is not initialized');
		}

		return this.serializer.serialize(this.view.state.doc);
	}

	serializeToJson() {
		if (!this.view?.state) {
			throw new Error('Editor state is not initialized');
		}

		return this.view.state.doc.toJSON() as object;
	}

	renderToHtml() {
		if (!this.view?.state) {
			throw new Error('Editor state is not initialized');
		}

		const div = document.createElement('div');
		const view = new EditorView(div, {
			state: this.view.state,
			nodeViews: this.nodeViews,
		});
		const html = view.dom.innerHTML;
		view.destroy();

		return html;
	}

	private createExtensions(
		extensions?: Array<Extension | Node | Mark>,
		disabledExtensions: string[] = [],
		disableInputExtensions: string[] = []
	) {
		const allExtensions = [
			...[new Doc(), new Paragraph(), new Text(), ...(extensions ?? [])],
		] as (Node | Mark | Extension)[];

		// Need a list of extension before constructing Code plugin type.
		// Can't construct excludes on the fly
		allExtensions.push(
			// Code mark exclude all marks but comment
			new Code({
				excludes: allExtensions
					.filter((ext) => ext.type === 'mark' && ext.name !== 'comment')
					.map((ext) => ext.name)
					.join(' '),
			})
		);

		allExtensions.push(
			// This plugin has to be the last one or it will block the Tab shortcut in other nodes/extensions
			new PreventTab({})
		);

		// mocking out the RichMarkdownEditor react component for now
		return new ExtensionManager(
			allExtensions,
			disabledExtensions,
			disableInputExtensions,
			this
		);
	}

	private createPlugins() {
		return this.extensions.plugins;
	}

	private createRulePlugins() {
		return this.extensions.rulePlugins;
	}

	private createInputRules() {
		return this.extensions.inputRules({
			schema: this.schema,
		});
	}

	private createKeymaps() {
		return this.extensions.keymaps({
			schema: this.schema,
		});
	}

	private createNodes() {
		return this.extensions.nodes;
	}

	private createMarks() {
		return this.extensions.marks;
	}

	private createWidgets() {
		return this.extensions.widgets;
	}

	private createSchema() {
		return new Schema({
			nodes: this.nodes,
			marks: this.marks,
		});
	}

	private createSerializer() {
		return this.extensions.serializer();
	}

	private createParser() {
		return this.extensions.parser({
			schema: this.schema,
			plugins: this.rulePlugins,
		});
	}

	private createPasteParser() {
		return this.extensions.parser({
			schema: this.schema,
			rules: { linkify: true, disable: ['emoji'] },
			plugins: this.rulePlugins,
		});
	}

	private createState(value?: string) {
		const doc = this.createDocument(value ?? '');

		if (isNil(doc)) {
			const error = new Error('Failed to parse document');
			captureError(error);
			throw error;
		}

		return EditorState.create({
			schema: this.schema,
			doc,
			plugins: [
				...this.plugins,
				...this.keymaps,
				dropCursor({ color: light.cursor }),
				gapCursor(),
				inputRules({
					rules: this.inputRules,
				}),
				keymap(baseKeymap),
			],
		});
	}

	private createDocument(content: string) {
		return this.parser.parse(content) ?? undefined;
	}

	private createNodeViews() {
		return (
			this.extensions.extensions.filter(
				(extension: Node | Mark | Extension) =>
					extension instanceof ProseMirrorReactNode && !!extension.component
			) as Array<ProseMirrorReactNode>
		).reduce((nodeViews, extension: ProseMirrorReactNode) => {
			const nodeView = (
				node: ProsemirrorNode,
				view: EditorView,
				getPos: () => number,
				decorations: Array<Decoration>
			) =>
				new ComponentView({
					component: extension.component,
					editor: this,
					node,
					view,
					getPos,
					decorations,
				});

			return {
				...nodeViews,
				[extension.name]: nodeView,
			};
		}, {});
	}

	createView(element?: HTMLElement | null, value?: string) {
		if (this.view) {
			// eslint-disable-next-line no-console
			console.log('update view', value);
			this.state = this.createState(value);
			this.view.updateState(this.state);
			return this.view;
		}

		if (!element) {
			throw new Error('createView called before ref available');
		}

		const isEditingCheckbox = (tr: Transaction) =>
			tr.steps.some(
				(step: any) =>
					this.schema.nodes?.checkbox_item?.name &&
					step.slice?.content?.firstChild?.type.name ===
						this.schema.nodes.checkbox_item?.name
			);

		const isCommentTransaction = (tr: Transaction) =>
			tr.steps.some(
				(step: any) =>
					(step instanceof AddMarkStep || step instanceof RemoveMarkStep) &&
					step.mark.type.name === 'comment'
			);

		const self = this;
		self.state = self.createState(value);
		this.view = new EditorView(element, {
			state: self.state,
			editable: () => !this.options.readOnly,
			nodeViews: this.nodeViews,
			dispatchTransaction(transaction) {
				// Callback is bound to have the view instance as its this binding
				const { state, transactions } =
					this.state.applyTransaction(transaction);

				// @ts-expect-error TS(2339): Property 'updateState' does not exist on type 'Dir... Remove this comment to see the full error message
				this.updateState(state);

				// If any of the transactions being dispatched resulted in the doc
				// changing then call our own change handler to let the outside world
				// know
				const canEditDoc =
					self.view?.editable ||
					transactions.some(isCommentTransaction) ||
					transactions.some(isEditingCheckbox);

				if (transactions.some((tr) => tr.docChanged) && canEditDoc) {
					self.options.handleChange?.(self.serializeToMarkdown());
				}

				self.onStateChange?.();
			},
		});
		// Tell third-party libraries and screen-readers that this is an input
		this.view.dom.setAttribute('role', 'textbox');

		this.commands = this.extensions.commands({
			schema: this.schema,
			view: this.view,
		});

		return this.view;
	}

	triggerMentionMenu() {
		if (!this.view) {
			return;
		}

		this.view.dispatch(this.view.state.tr.insertText('@'));
		this.view.focus();
		this.openToolbar(Toolbar.Mention);
	}

	clear() {
		if (!this.view) {
			return;
		}

		this.view.dispatch(
			this.view.state.tr.delete(0, this.view.state.doc.content.size)
		);
	}

	appendText(text: string) {
		if (!this.view) {
			return;
		}

		this.view.dispatch(this.view.state.tr.insertText(text));
		this.view.focus();
	}

	focus() {
		if (!this.view) {
			return;
		}

		this.view.focus();
	}

	focusAtStart() {
		const selection = Selection.atStart(this.view!.state.doc);
		const transaction = this.view!.state.tr.setSelection(selection);
		this.view!.dispatch(transaction);
		this.view!.focus();
	}

	focusAtEnd() {
		const selection = Selection.atEnd(this.view!.state.doc);
		const transaction = this.view!.state.tr.setSelection(selection);
		this.view!.dispatch(transaction);
		this.view!.focus();
	}

	closeToolbars(toolbar?: Toolbar) {
		if (toolbar === this.openedToolbar || !toolbar) {
			this.openedToolbar = null;
			this.blockMenuSearch = '';

			this.onStateChange?.();
		}
	}

	openToolbar(toolbar: Toolbar, blockMenuSearch?: string) {
		this.openedToolbar = toolbar;
		this.blockMenuSearch = blockMenuSearch;
		this.onStateChange?.();
	}

	setBlockMenuSearch(search: string) {
		this.blockMenuSearch = search;
		this.onStateChange?.();
	}

	setInitialFocus() {
		if (!this.view) {
			return;
		}

		if (this.options.scrollTo) {
			this.scrollToAnchor(this.options.scrollTo!);
		} else if (!this.options.readOnly && this.options.autoFocus) {
			this.focusAtEnd();
		}
	}

	scrollToAnchor(hash: string) {
		if (!hash || !this.view) {
			return;
		}

		const nodeIdToSearch = hash.substring(1);

		const headings = getHeadings(this.view.state.doc);
		const queryBlocks = getQueryBlocks(this.view.state.doc);
		const activeHeading =
			headings.find(
				(heading) =>
					heading.id === nodeIdToSearch || heading.legacyId === nodeIdToSearch
			) ||
			queryBlocks.find((queryBlock) => queryBlock.linkId === nodeIdToSearch);

		if (activeHeading) {
			this.view.focus();
			this.view.dispatch(
				this.view.state.tr
					.setSelection(activeHeading.selection)
					.scrollIntoView()
			);
			return;
		}

		const activeCommentSelection = getCommentFromId(
			this.view.state.doc,
			nodeIdToSearch
		);
		if (activeCommentSelection) {
			this.view.focus();
			this.view.dispatch(
				this.view.state.tr.setSelection(activeCommentSelection).scrollIntoView()
			);
			return;
		}
	}
}
